import type { Signal } from '@angular/core'
import { computed, inject, Injectable } from '@angular/core'
import type { DocumentSpace } from '@awork/features/document/models/document-space.model'
import { DocumentSpaceStore } from '@awork/features/document/state/document-space.store'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { SelectOptions } from '@awork/core/state/signal-store/types'
import { Order } from '@awork/core/state/signal-store/types'
import type { AccessLevels } from '@awork/features/workspace/models/permissions.model'
import { hasAccessLevelOrMore } from '@awork/features/workspace/models/permissions.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import { FeatureId } from '@awork/_shared/models/subscription-plan.model'
import { SubscriptionPlanQuery } from '@awork/_shared/state/subscription-plan.query'
import { TeamQuery } from '@awork/features/team/state/team.query'

@Injectable({ providedIn: 'root' })
export class DocumentSpaceQuery extends EntitySignalQuery<DocumentSpace> {
  protected store: DocumentSpaceStore

  private userQuery = inject(UserQuery)
  private permissionsQuery = inject(PermissionsQuery)
  private subscriptionPlanQuery = inject(SubscriptionPlanQuery)
  private teamQuery = inject(TeamQuery)

  constructor() {
    const store = inject(DocumentSpaceStore)

    super(store)

    this.store = store
  }

  /**
   * Gets a document space by id
   * @param {string} id
   * @returns {Signal<DocumentSpace>}
   */
  queryDocumentSpace(id: string): Signal<DocumentSpace> {
    return computed(() => {
      const documentSpace = this.queryEntity(id)

      const documentSpaceEntity = documentSpace()

      if (documentSpaceEntity) {
        documentSpaceEntity.teams = documentSpaceEntity.teams.map(teamAccess => {
          const team = this.teamQuery.getTeam(teamAccess.teamId)
          return { ...teamAccess, team }
        })
      }

      return documentSpaceEntity
    })
  }

  /**
   * Gets document spaces applying the select options
   * @param {SelectOptions<DocumentSpace> & { searchQuery?: string; }} options
   * @returns {Signal<DocumentSpace[]>}
   * @retuns {Signal<DocumentSpace[]>}
   */
  queryDocumentSpaces(options?: SelectOptions<DocumentSpace> & { searchQuery?: string }): Signal<DocumentSpace[]> {
    return computed(() => {
      const documentSpaces = this.queryAll({
        filterBy: options?.filterBy || (documentSpace => this.searchFilter(documentSpace, options?.searchQuery)),
        sortBy: options?.sortBy || 'order',
        sortByOrder: options?.sortByOrder || Order.ASC,
        limitTo: options?.limitTo
      })

      const documentSpacesEntities = documentSpaces()

      documentSpacesEntities.forEach(documentSpace => {
        if (documentSpace) {
          documentSpace.teams = documentSpace.teams.map(teamAccess => {
            const team = this.teamQuery.getTeam(teamAccess.teamId)
            return { ...teamAccess, team }
          })
        }
      })

      return this.mapEntities(documentSpacesEntities)
    })
  }

  /**
   * Gets whether or not the user is allowed to read or manage(edit) documents of a space
   * @param {string} documentSpaceId
   * @param {AccessLevels} accessLevel
   * @returns {Signal<boolean>}
   */
  queryIsAllowed(documentSpaceId: string, accessLevel: AccessLevels): Signal<boolean> {
    return computed(() => {
      const documentSpace = this.queryDocumentSpace(documentSpaceId)
      const currentUser = this.userQuery.queryCurrentUser()
      const userPermissions = this.permissionsQuery.queryUserPermissions()
      const isFeatureAvailable = this.subscriptionPlanQuery.queryIsPlanFeatureAvailable(FeatureId.DocumentSpaces)

      if (!documentSpace()) {
        return false
      }

      // Check if current user is an admin and the feature is available for the plan
      if (userPermissions().isAdmin && isFeatureAvailable()) {
        return true
      }

      // Check if the space has workspace access level
      const hasWorkspaceLevelAccess = hasAccessLevelOrMore(documentSpace().workspaceAccessLevel, accessLevel)

      if (hasWorkspaceLevelAccess) {
        return true
      }

      // Check if user is in a team that has access
      const spaceTeams = documentSpace().teams.filter(teamAccess =>
        teamAccess.team?.users?.some(user => user.id === currentUser().id)
      )

      if (spaceTeams.length) {
        const hasTeamAccess = spaceTeams.some(spaceTeam => hasAccessLevelOrMore(spaceTeam.accessLevel, accessLevel))

        if (hasTeamAccess) {
          return true
        }
      }

      // Check if user is a contributor
      const spaceContributor = documentSpace().contributors.find(contributor => contributor.userId === currentUser().id)

      if (spaceContributor) {
        const hasContributorAccess = hasAccessLevelOrMore(spaceContributor.accessLevel, accessLevel)

        if (hasContributorAccess) {
          return true
        }
      }

      return false
    })
  }

  /**
   * Filter used for search by name
   * @param {DocumentSpace} documentSpace
   * @param {string} searchQuery
   * @returns {boolean}
   */
  private searchFilter(documentSpace: DocumentSpace, searchQuery: string): boolean {
    return !searchQuery || documentSpace.name.toLowerCase().includes(searchQuery.toLowerCase())
  }
}
